import { z } from 'zod/v4';
import { WORKOUT_STATUSES } from '../../block/workout-status/types';
export const WORKOUT_STATUS_TASK_TYPE = 'WorkoutStatusTask';
export const zWorkoutStatusSource = z.enum(['mobile', 'web']);
/** The status the transition landed on — single-sourced from the domain. */
export const zWorkoutStatusAction = z.enum(WORKOUT_STATUSES);
/**
 * Pointer to a workout-status transition, not an event: the integrations
 * worker re-reads state at processing time. Two reconstruction reads, both
 * keyed by fields this task carries:
 *
 * - Current status, by (orgId, blockId, workoutId, profileId) against the
 *   isCurrent published layer — decides emit vs reverse.
 * - Content as it was at the transition, by the pinned versions:
 *   (orgId, blockId, workoutId, profileId, athleteVersion) addresses the
 *   athlete's published layer row and blockVersion the block's — both
 *   append-only versioned tables, so the completed workout is exact even
 *   after later publishes.
 *
 * Unknown keys are stripped, not rejected, so additive producers can extend
 * the payload without a coordinated deploy. The type literal is the contract
 * version: a breaking change ships as a new type literal, not a version
 * field.
 */
export const zWorkoutStatusTaskInput = z.object({
    taskId: z.uuid(),
    orgId: z.uuid(),
    blockId: z.uuid(),
    workoutId: z.uuid(),
    profileId: z.uuid(),
    /**
     * The profile who performed the transition — the athlete, or the coach
     * in bulk actuals; whose local time occurredAt carries.
     */
    updatedById: z.uuid(),
    source: zWorkoutStatusSource,
    /** The status the transition landed on. */
    action: zWorkoutStatusAction,
    /**
     * The updatedById profile's local time at the transition, ISO 8601 with
     * offset — never server UTC; badge windows are local-calendar.
     */
    occurredAt: z.iso.datetime({ offset: true }),
    /**
     * The IANA zone the command derived `occurredAt` from — the acting
     * profile's web/mobile device zone. Absent when the command carried no
     * usable zone and the emission fell back to UTC.
     */
    timezone: z.string().optional(),
    /** The published-layer content versions at the transition. */
    blockVersion: z.number().int().positive(),
    athleteVersion: z.number().int().positive(),
});
export const zWorkoutStatusTask = z.object({
    type: z.literal(WORKOUT_STATUS_TASK_TYPE),
    input: zWorkoutStatusTaskInput,
});
/**
 * FIFO MessageGroupId: ordering is preserved per athlete-workout, so a reversal
 * can never overtake the completion it reverses, while different athletes on a
 * shared workout stay independent.
 */
export function workoutStatusMessageGroupId(data) {
    return `${data.input.workoutId}:${data.input.profileId}`;
}
/**
 * FIFO MessageDeduplicationId: the task's own id, so a redelivered message is
 * deduplicated by SQS. The integration-level idempotency key is built later,
 * when the event is constructed.
 */
export function workoutStatusMessageDeduplicationId(data) {
    return data.input.taskId;
}
//# sourceMappingURL=workout-status-task.js.map